import type { LocationSlot } from './types.js';
import { getCurrentOffset, getAbbr, formatOffset, toLocal, isWorkingMinutes, getOverlapBlocks } from './timezone.js';

// Distinct colors for each location — must match LOCATION_COLORS in ui.ts
const LOCATION_COLORS = [
  '#6366f1', '#0B6B62', '#C97A16', '#B4437F', '#7C5CD6', '#1D7FA3',
  '#B4433A', '#5E7F1E', '#8B4FBF', '#146C8C', '#C25E1C', '#1F7A55',
];

export interface TimelineContext {
  slots: LocationSlot[];
  /** Instant used for offset math */
  at: Date;
  /** Whether the reference date is today (controls the "now" marker) */
  isToday: boolean;
  /** UTC hour of the top-ranked option, highlighted on the chart */
  bestHour?: number;
}

/**
 * The chart's colours, resolved from the stylesheet.
 *
 * Declared as a fixed shape rather than a string map: under
 * `noUncheckedIndexedAccess` an index signature would widen every lookup to
 * `string | undefined`, which the canvas API rejects.
 */
interface Palette {
  surface: string;
  sunken: string;
  ink: string;
  muted: string;
  line: string;
  lineFaint: string;
  good: string;
  accent: string;
  bad: string;
}

/**
 * Palette comes from the stylesheet rather than constants, so the chart follows
 * the theme — including the system-preference case, where no data-theme
 * attribute exists to branch on.
 */
function palette(): Palette {
  const cs = getComputedStyle(document.documentElement);
  const v = (name: string, fallback: string) => (cs.getPropertyValue(name).trim() || fallback);
  return {
    surface: v('--surface', '#ffffff'),
    sunken: v('--sunken', '#f4f2ec'),
    ink: v('--ink', '#16211f'),
    muted: v('--ink-4', '#8a9694'),
    line: v('--line', '#e5e2da'),
    lineFaint: v('--line-3', '#f2f0ea'),
    good: v('--good', '#2f7d52'),
    accent: v('--accent', '#c97a16'),
    bad: v('--bad', '#b4433a'),
  };
}

/** Format a local hour (0-23) as "9 AM" */
function formatLocalHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

/** Draw the 24-hour timeline on a canvas */
export function drawTimeline(canvas: HTMLCanvasElement, tctx: TimelineContext): void {
  const { slots, at, isToday, bestHour } = tctx;
  const ctx = canvas.getContext('2d');
  if (!ctx || slots.length === 0) return;

  const c = palette();

  // Responsive layout: the canvas fills its scroll container, with a floor for
  // legibility that turns the container into a horizontal scroller.
  const container = canvas.parentElement!;
  const cs = getComputedStyle(container);
  const padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
  const available = container.clientWidth - padX;
  const isCompact = available < 560;
  const width = Math.max(available, isCompact ? 480 : 620);

  const leftPadding = isCompact ? 88 : 140;
  const rightPadding = isCompact ? 8 : 16;
  const topPadding = 30;
  const rowHeight = isCompact ? 44 : 58;
  const rowGap = 10;
  const legendHeight = 34;
  const height = topPadding + 22 + slots.length * (rowHeight + rowGap) + legendHeight;

  const dpr = window.devicePixelRatio || 1;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const chartWidth = width - leftPadding - rightPadding;
  const colW = chartWidth / 24;

  const mono = (size: string) => `${size} ui-monospace, 'SF Mono', 'Roboto Mono', monospace`;
  const sans = (size: string) => `${size} Inter, sans-serif`;

  // Background — opaque, because the canvas is also the copy-image export.
  ctx.fillStyle = c.surface;
  ctx.fillRect(0, 0, width, height);

  // === UTC hour scale ===
  ctx.textAlign = 'center';
  for (let h = 0; h < 24; h++) {
    const isMajor = h % 3 === 0;
    if (!isMajor && isCompact) continue;
    ctx.fillStyle = isMajor ? c.ink : c.muted;
    ctx.font = mono(isMajor ? '500 10px' : '9px');
    ctx.fillText(String(h).padStart(2, '0'), leftPadding + h * colW + colW / 2, topPadding - 12);
  }
  ctx.textAlign = 'right';
  ctx.font = sans('600 9px');
  ctx.fillStyle = c.muted;
  ctx.fillText('UTC', leftPadding - 12, topPadding - 12);

  const gridTop = topPadding - 4;
  const gridBottom = topPadding + 4 + slots.length * (rowHeight + rowGap) - rowGap;

  for (let h = 0; h <= 24; h++) {
    const x = Math.round(leftPadding + h * colW) + 0.5;
    ctx.strokeStyle = h % 3 === 0 ? c.line : c.lineFaint;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, gridTop);
    ctx.lineTo(x, gridBottom);
    ctx.stroke();
  }

  const overlap = getOverlapBlocks(slots, at);

  // === One row per location ===
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i]!;
    const color = LOCATION_COLORS[i % LOCATION_COLORS.length]!;
    const offset = getCurrentOffset(slot.timezone, at);
    const y = topPadding + 6 + i * (rowHeight + rowGap);

    // Label column: colour rule, city, and the abbreviation in effect on the
    // reference date (a chart showing July must say EDT, not EST).
    ctx.fillStyle = color;
    ctx.fillRect(0, y + 4, 3, rowHeight - 8);
    ctx.textAlign = 'left';
    ctx.fillStyle = c.ink;
    ctx.font = sans(`600 ${isCompact ? '11px' : '12.5px'}`);
    ctx.fillText(truncate(ctx, slot.city.name, leftPadding - 24), 12, y + rowHeight / 2 - 3);
    ctx.fillStyle = c.muted;
    ctx.font = mono('9px');
    ctx.fillText(`${getAbbr(slot.timezone, at)} ${formatOffset(offset)}`, 12, y + rowHeight / 2 + 11);

    for (let h = 0; h < 24; h++) {
      const localMin = toLocal(h, offset).minutes;
      const working = isWorkingMinutes(localMin, slot.workingStart, slot.workingEnd);
      const x = leftPadding + h * colW;

      if (working) {
        // Everyone-working hours are called out in amber; the rest are washed
        // in the location's own colour.
        ctx.fillStyle = overlap[h] ? c.accent : color;
        ctx.globalAlpha = overlap[h] ? 0.26 : 0.16;
        ctx.fillRect(x + 1, y + 8, colW - 2, rowHeight - 16);
        ctx.globalAlpha = 1;
        ctx.fillStyle = overlap[h] ? c.accent : color;
        ctx.fillRect(x + 1, y + rowHeight - 10, colW - 2, 2);
      } else {
        ctx.fillStyle = c.sunken;
        ctx.fillRect(x + 1, y + 8, colW - 2, rowHeight - 16);
      }

      // Anchor the row in local time: label local noon and midnight only.
      const localHour = Math.round(localMin / 60) % 24;
      if (!isCompact && localMin % 720 === 0) {
        ctx.fillStyle = working ? color : c.muted;
        ctx.font = mono('8.5px');
        ctx.textAlign = 'center';
        ctx.fillText(formatLocalHour(localHour), x + colW / 2, y + rowHeight / 2 + 1);
        ctx.textAlign = 'left';
      }
    }
  }

  // === Recommended hour ===
  if (bestHour !== undefined) {
    const bx = Math.round(leftPadding + bestHour * colW) + 0.5;
    ctx.fillStyle = c.good;
    ctx.globalAlpha = 0.08;
    ctx.fillRect(bx, gridTop, colW, gridBottom - gridTop);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = c.good;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(bx, gridTop, colW, gridBottom - gridTop);
  }

  // === Now marker (only meaningful when showing today) ===
  const now = new Date();
  const utcHour = now.getUTCHours() + now.getUTCMinutes() / 60;
  const nowX = leftPadding + utcHour * colW;

  if (isToday) {
    ctx.strokeStyle = c.bad;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(nowX, gridTop);
    ctx.lineTo(nowX, gridBottom);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = c.bad;
    ctx.beginPath();
    ctx.arc(nowX, gridTop, 3, 0, Math.PI * 2);
    ctx.fill();
  }

  // === Legend ===
  const legendY = gridBottom + 22;
  let lx = leftPadding;
  ctx.textAlign = 'left';

  const item = (swatch: (x: number, y: number) => void, label: string) => {
    swatch(lx, legendY - 9);
    ctx.fillStyle = c.muted;
    ctx.font = sans('11px');
    ctx.fillText(label, lx + 18, legendY);
    lx += 18 + ctx.measureText(label).width + 22;
  };

  if (overlap.some(Boolean)) {
    item((x, y) => {
      ctx.fillStyle = c.accent;
      ctx.globalAlpha = 0.3;
      ctx.fillRect(x, y, 12, 12);
      ctx.globalAlpha = 1;
      ctx.fillStyle = c.accent;
      ctx.fillRect(x, y + 10, 12, 2);
    }, 'Everyone working');
  }
  if (bestHour !== undefined) {
    item((x, y) => {
      ctx.strokeStyle = c.good;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x + 0.5, y + 0.5, 11, 11);
    }, 'Recommended hour');
  }
  if (isToday) {
    item((x, y) => {
      ctx.strokeStyle = c.bad;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x + 6, y);
      ctx.lineTo(x + 6, y + 12);
      ctx.stroke();
      ctx.setLineDash([]);
    }, 'Now');
  }
  item((x, y) => {
    ctx.fillStyle = c.sunken;
    ctx.fillRect(x, y, 12, 12);
  }, 'Outside working hours');

  // Centre whichever marker is most relevant when the chart overflows.
  const focusX = isToday ? nowX : bestHour !== undefined ? leftPadding + bestHour * colW : 0;
  if (focusX > 0) {
    const target = focusX - container.clientWidth / 2;
    container.scrollLeft = Math.max(0, Math.min(target, Math.max(0, width - container.clientWidth)));
  }
}

/** Trim a label to fit the fixed label column instead of letting it collide. */
function truncate(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let out = text;
  while (out.length > 3 && ctx.measureText(out + '…').width > maxWidth) out = out.slice(0, -1);
  return out + '…';
}
